import { getApiBaseUrl } from "./apiBaseUrl";
import { generateFrontendJwtPerRequest } from "@/utils/jwtClient";
import { emitApiFailure } from "@/context/apiFailureBus";

const DEFAULT_TIMEOUT_MS = 10_000;

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public detail?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type ApiResult<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  timeoutMs?: number;
  /** Skip attaching the frontend service JWT (rare; most routes require it). */
  skipAuth?: boolean;
};

function buildUrl(path: string): string {
  const base = getApiBaseUrl();
  const endpoint = path.startsWith("/") ? path : `/${path}`;
  return `${base}${endpoint}`;
}

function isAuthFailure(status: number, detail: unknown): boolean {
  if (status !== 401) return false;
  const message =
    typeof detail === "string"
      ? detail
      : (detail as { detail?: string })?.detail || "";
  return /invalid token|expired|authorization/i.test(message);
}

async function buildAuthHeaders(
  headers: HeadersInit | undefined,
  skipAuth: boolean
): Promise<Headers> {
  const merged = new Headers(headers);
  if (skipAuth || merged.has("Authorization")) {
    return merged;
  }
  const token = await generateFrontendJwtPerRequest();
  if (token) {
    merged.set("Authorization", `Bearer ${token}`);
  }
  return merged;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    body,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    headers,
    skipAuth = false,
    ...init
  } = options;
  const url = buildUrl(path);

  const execute = async (retryOnAuthFailure: boolean): Promise<T> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const authHeaders = await buildAuthHeaders(headers, skipAuth);
      if (!authHeaders.has("Content-Type")) {
        authHeaders.set("Content-Type", "application/json");
      }

      const response = await fetch(url, {
        ...init,
        signal: controller.signal,
        headers: authHeaders,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        let detail: unknown;
        let message = `HTTP ${response.status}`;
        try {
          const errBody = await response.json();
          detail = errBody;
          message =
            (errBody as { detail?: string }).detail ||
            (errBody as { message?: string }).message ||
            message;
        } catch {
          /* ignore */
        }

        if (retryOnAuthFailure && isAuthFailure(response.status, detail)) {
          return execute(false);
        }

        const apiErr = new ApiError(message, response.status, detail);
        
        if (isAuthFailure(response.status, detail)) {
          window.dispatchEvent(new Event('auth:unauthorized'));
        }
        
        throw apiErr;
      }

      if (response.status === 204) {
        return undefined as T;
      }
      return (await response.json()) as T;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (error instanceof Error && error.name === "AbortError") {
        const timeoutErr = new ApiError("Request timed out");
        emitApiFailure(timeoutErr);
        throw timeoutErr;
      }
      const genericErr = new ApiError(
        error instanceof Error ? error.message : "Network request failed"
      );
      emitApiFailure(genericErr);
      throw genericErr;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  return execute(true);
}

export async function requestResult<T>(
  path: string,
  options: RequestOptions = {}
): Promise<ApiResult<T>> {
  try {
    const data = await request<T>(path, options);
    return { success: true, data };
  } catch (error) {
    const message =
      error instanceof ApiError
        ? error.message
        : error instanceof Error
          ? error.message
          : "Request failed";
    return { success: false, error: message };
  }
}

export { getApiBaseUrl, buildUrl };
